import { Box, Grid, useMediaQuery, useTheme } from '@mui/material';
import { H5, Small } from 'components/Typography';
import { MessageCircle, Star, ThumbsUp } from 'lucide-react';
import { useState } from 'react';

const BookmarkedPostsItem = ({ item, onClick, ...props }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [isStarred, setIsStarred] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [starCount, setStarCount] = useState(item.starCount || 0);
  const [likeCount, setLikeCount] = useState(item.likeCount || 0);

  const handleClick = () => {
    if (onClick) {
      onClick(item);
    }
  };

  const handleStarClick = e => {
    e.stopPropagation();
    setIsStarred(!isStarred);
    setStarCount(prev => (isStarred ? prev - 1 : prev + 1));
  };

  const handleLikeClick = e => {
    e.stopPropagation();
    setIsLiked(!isLiked);
    setLikeCount(prev => (isLiked ? prev - 1 : prev + 1));
  };

  return (
    <>
      <Grid onClick={handleClick} item xs={12} key={item.id} {...props}>
        <Box
          sx={{
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            '&:hover': isMobile
              ? {}
              : {
                  transform: 'translateY(-2px)',
                },
          }}
        >
          {/* Top Section: Title + Stats */}
          <Box display="flex" flexDirection="column" gap={1} width="100%">
            {/* Title Row */}
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  backgroundColor: theme.palette.primary.mainBlue,
                  color: theme.palette.primary.white,
                  padding: '1px 8px',
                  borderRadius: '100px',
                  fontSize: '12px',
                  fontWeight: '600',
                  display: 'inline-block',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                }}
              >
                {item.category}
              </Box>
              <H5
                sx={{
                  fontWeight: 'bold',
                  fontSize: '15px',
                  color: theme.palette.text.primary,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {item.title}
              </H5>
            </Box>

            {/* Stats Row */}
            <Box display="flex" alignItems="center" gap={1} width="100%">
              {/* Star */}
              <Box
                display="flex"
                alignItems="center"
                gap={0.5}
                onClick={handleStarClick}
              >
                <Star
                  size={16}
                  color={
                    isStarred
                      ? theme.palette.primary.mainBlue
                      : theme.palette.primary.greytxt
                  }
                  fill={isStarred ? theme.palette.primary.mainBlue : 'none'}
                />
                <Small
                  sx={{
                    color: isStarred
                      ? theme.palette.primary.mainBlue
                      : theme.palette.primary.greytxt,
                  }}
                >
                  {starCount}
                </Small>
              </Box>
              {/* Like */}
              <Box
                display="flex"
                alignItems="center"
                gap={0.5}
                onClick={handleLikeClick}
              >
                <ThumbsUp
                  size={16}
                  color={
                    isLiked
                      ? theme.palette.primary.mainBlue
                      : theme.palette.primary.greytxt
                  }
                  fill={isLiked ? theme.palette.primary.mainBlue : 'none'}
                />
                <Small
                  sx={{
                    color: isLiked
                      ? theme.palette.primary.mainBlue
                      : theme.palette.primary.greytxt,
                  }}
                >
                  {likeCount}
                </Small>
              </Box>
              {/* Comment */}
              <Box display="flex" alignItems="center" gap={0.5}>
                <MessageCircle
                  size={16}
                  color={theme.palette.primary.greytxt}
                />
                <Small sx={{ color: theme.palette.primary.greytxt }}>
                  {item.commentCount}
                </Small>
              </Box>
              {/* Date */}
              <Small sx={{ color: theme.palette.primary.greytxt }}>
                {item.date}
              </Small>
            </Box>

            {/* Content Section: Thumbnail + Text */}
            <Box display="flex" gap={2} alignItems="flex-start" mt={0.5}>
              {/* Thumbnail */}
              {item.thumbnail && item.thumbnail.trim() !== '' && (
                <Box
                  sx={{
                    width: isMobile ? '48px' : '48px',
                    height: isMobile ? '48px' : '48px',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    flexShrink: 0,
                    backgroundColor: '#f5f5f5',
                  }}
                >
                  <img
                    src={item.thumbnail}
                    alt="thumbnail"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                </Box>
              )}

              {/* Content Text */}
              <Small
                sx={{
                  color: theme.palette.primary.greytxt,
                  lineHeight: 1.4,
                  fontSize: '13px',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  flex: 1,
                }}
              >
                {item.content}
              </Small>
            </Box>
          </Box>
        </Box>
      </Grid>
      {/* Divider */}
      <Grid item xs={12}>
        <Box
          sx={{
            height: '1px',
            backgroundColor: theme.palette.borderColor,
            margin: '0',
            width: '100%',
          }}
        />
      </Grid>
    </>
  );
};

export default BookmarkedPostsItem;
